/**
 * Reusable bottom-sheet primitive matching the SignInModal pattern (scrim +
 * drag-handle + slide-up). Accepts a controlled `open` flag so callers can
 * keep their own state (e.g. in HomeScreen) without prop-drilling.
 *
 * Renders nothing when `open` is false so the animations only run on
 * open transitions.
 */
import { useEffect, type ReactNode } from "react";

export interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  ariaLabel: string;
  children: ReactNode;
  /** Set to false on flows where dismissal should require an explicit action. */
  dismissable?: boolean;
}

export function BottomSheet({
  open,
  onClose,
  ariaLabel,
  children,
  dismissable = true,
}: BottomSheetProps) {
  // Close on ESC for keyboard users.
  useEffect(() => {
    if (!open || !dismissable) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, dismissable, onClose]);

  if (!open) return null;

  return (
    <>
      <div
        onClick={dismissable ? onClose : undefined}
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(10, 14, 12, 0.45)",
          backdropFilter: "blur(2px)",
          WebkitBackdropFilter: "blur(2px)",
          zIndex: 30,
          animation: "fadeIn 180ms ease",
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          maxHeight: "calc(100% - 24px)",
          overflowY: "auto",
          background: "var(--surface-1)",
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          padding: "16px 18px calc(env(safe-area-inset-bottom, 0px) + 22px)",
          boxShadow: "0 -10px 30px -10px rgba(0,0,0,0.25)",
          zIndex: 31,
          animation: "slideUp 220ms cubic-bezier(0.2, 0.8, 0.2, 1)",
        }}
      >
        <div
          aria-hidden
          style={{
            width: 40,
            height: 4,
            borderRadius: 999,
            background: "var(--surface-2-strong)",
            margin: "2px auto 14px",
          }}
        />
        {children}
      </div>
    </>
  );
}
